
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { CardFormValues } from '@/components/auth/CardVerificationForm';
import { UserFormValues } from '@/components/auth/UserInfoForm';
import { useSignupSubmit } from './useSignupSubmit';
import { useCardVerification } from '@/hooks/useCardVerification';
import { useBitcoinVerification } from '@/hooks/useBitcoinVerification';
import { useSignUpFormState } from './useSignUpFormState';
import { usePolicyVerificationState } from './usePolicyVerificationState';
import { UseSignUpReturn } from './types';
import { CardDetails as PaymentCardDetails } from '@/types/payment';

export const useSignUp = (): UseSignUpReturn => {
  const navigate = useNavigate();
  const formState = useSignUpFormState();
  const policyState = usePolicyVerificationState();
  const { handleSignup, checkUserExists, signInUser } = useSignupSubmit();
  
  // Card verification
  const {
    isVerifying: isCardVerifying,
    isVerified: isCardVerified,
    verificationScore: cardVerificationScore,
    riskLevel: cardRiskLevel,
    verificationError: cardVerificationError,
    verifyCard,
    resetVerification: resetCardVerification,
    onVerificationComplete: onCardVerificationComplete,
    updateCardData
  } = useCardVerification();
  
  // Bitcoin verification
  const {
    isVerifying: isBitcoinVerifying,
    isVerified: isBitcoinVerified,
    verificationScore: bitcoinVerificationScore,
    riskLevel: bitcoinRiskLevel,
    verificationError: bitcoinVerificationError,
    verifyBitcoin,
    resetVerification: resetBitcoinVerification,
    onVerificationComplete: onBitcoinVerificationComplete,
  } = useBitcoinVerification();
  
  // Combined verification state
  const isVerifying = formState.formData.verificationMethod === 'card' ? isCardVerifying : isBitcoinVerifying;
  const isVerified = formState.formData.verificationMethod === 'card' ? isCardVerified : isBitcoinVerified;
  const verificationScore = formState.formData.verificationMethod === 'card' ? cardVerificationScore : bitcoinVerificationScore;
  const riskLevel = formState.formData.verificationMethod === 'card' ? cardRiskLevel : bitcoinRiskLevel;
  const verificationError = formState.formData.verificationMethod === 'card' ? cardVerificationError : bitcoinVerificationError;

  const handleUserFormSubmit = async (data: UserFormValues) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    formState.updateUserData(data);
    
    try {
      // Check if user exists during form submission
      const result = await checkUserExists(data.email);
      formState.updateUserExists(result.exists);
    } catch (error) {
      console.error("Error checking user existence:", error);
      // Continue to verification step even if check fails
      formState.setStep('card');
    }
  };

  const handleSigninFormSubmit = async (email: string, password: string) => {
    try {
      formState.setIsSigningIn(true);
      await signInUser(email, password);
      formState.setStep('card');
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      formState.setIsSigningIn(false);
    }
  };

  const handleCardFormSubmit = async (data: CardFormValues) => {
    if (isCardVerifying || policyState.isPolicyVerifying) return;
    
    // First update card data in state
    formState.updateCardData(data.card, data.termsAccepted);

    // Update card data for verification
    updateCardData(data.card);
    
    if (!policyState.isPolicyVerified) {
      // Start policy verification
      policyState.startPolicyVerification();
      return;
    }
    
    if (!isCardVerified) {
      // Verify the card when form is submitted
      try {
        // Ensure all required fields are present with saveCard property
        const cardDetails: PaymentCardDetails = {
          cardNumber: data.card.cardNumber || '',
          cardholderName: data.card.cardholderName || '',
          expiryDate: data.card.expiryDate || '',
          cvv: data.card.cvv || '',
          saveCard: data.card.saveCard !== undefined ? data.card.saveCard : true
        };
        
        console.log('Verifying card with data:', cardDetails);
        await verifyCard(cardDetails);
      } catch (error) {
        console.error('Card verification error:', error);
        toast.error('Card verification failed');
      }
      return;
    }
    
    await proceedAfterVerification();
  };

  const handleBitcoinVerification = async (bitcoinAddress: string, amount: number) => {
    if (isBitcoinVerifying || policyState.isPolicyVerifying) return;
    
    // Update bitcoin data in state
    formState.updateBitcoinData(bitcoinAddress);
    
    if (!policyState.isPolicyVerified) {
      // Start policy verification
      policyState.startPolicyVerification();
      return;
    }
    
    if (!isBitcoinVerified) {
      try {
        console.log('Verifying Bitcoin address:', bitcoinAddress);
        await verifyBitcoin(bitcoinAddress, amount);
      } catch (error) {
        console.error('Bitcoin verification error:', error);
        toast.error('Bitcoin verification failed');
      }
      return;
    }
    
    await proceedAfterVerification();
  };
  
  const proceedAfterVerification = async () => {
    try {
      // If the user exists, we'll skip the signup process
      if (formState.formData.userExists) {
        // Navigate directly to payment page since they're already logged in
        toast.success('Verification successful!');
        setTimeout(() => {
          navigate('/pin-transfer');
        }, 1000);
        return;
      }
      
      // Handle signup based on verification method
      if (formState.formData.verificationMethod === 'card') {
        await handleSignup(
          formState.formData.user, 
          formState.formData.card, 
          cardVerificationScore || undefined
        );
      } else {
        await handleSignup(
          formState.formData.user, 
          formState.formData.card, // Still needed for user account
          bitcoinVerificationScore || undefined,
          formState.formData.bitcoinAddress
        );
      }
      
      // Redirect to the transfer page after successful signup
      toast.success('Account created successfully!');
      
      // Brief timeout to ensure messages are seen before redirect
      setTimeout(() => {
        navigate('/pin-transfer');
      }, 1000);
      
    } catch (error: any) {
      console.error('Signup error:', error);
      toast.error(error.message || 'Signup failed. Please try again.');
    }
  };

  const handleRetry = () => {
    if (formState.formData.verificationMethod === 'card') {
      resetCardVerification();
    } else {
      resetBitcoinVerification();
    }
  };
  
  const handleMethodChange = (method: 'card' | 'bitcoin') => {
    formState.updateVerificationMethod(method);
  };

  return {
    step: formState.step,
    isVerifying,
    isVerified,
    isPolicyVerifying: policyState.isPolicyVerifying,
    isPolicyVerified: policyState.isPolicyVerified,
    verificationScore,
    riskLevel,
    policyComprehensionScore: policyState.policyComprehensionScore,
    policyFeedback: policyState.policyFeedback,
    verificationError,
    formData: formState.formData,
    isSigningIn: formState.isSigningIn,
    handleUserFormSubmit,
    handleSigninFormSubmit,
    handleCardFormSubmit,
    handleBitcoinVerification,
    onVerificationComplete: formState.formData.verificationMethod === 'card' 
      ? onCardVerificationComplete 
      : onBitcoinVerificationComplete,
    onPolicyVerificationComplete: policyState.onPolicyVerificationComplete,
    handleRetry,
    handleMethodChange
  };
};
